import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Checkbox } from 'primereact/checkbox';

import { translateError } from '../..';

import { LoginService } from './LoginService';
import { TenantService } from '../../service/TenantService';
import {
  ApplicationConfigurationService,
  storeApplicationLocalization,
} from '../../service/ApplicationConfigurationService';

import { AppStorage } from '../../service/UserStorage';
import { CultureService } from '../../service/CultureService';

import { ToastService } from '../../AppToast';
import { LanguageSwitcher } from '../../shared/language/LanguageSwitcher';
import { getStoredLocale, localized, useTranslation } from '../../shared/language';

export default function Login() {
  const { t } = useTranslation();

  const [submitted, setSubmitted] = useState(false);

  const [tenantName, setTenantName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    document.documentElement.style.fontSize = '14px';
    document.cookie = `__tenant=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;

    AppStorage.remove('tenant');
    AppStorage.remove('isHouseholdBusiness');
    const taxCode = getSubdomain();
    if (validateTaxCode(taxCode)) {
      setTenantName(taxCode);
    } else {
      if (AppStorage.get('tenantName')) {
        setTenantName(AppStorage.get('tenantName'));
      }
    }
    setSubmitted(true);
    LoginService.logout().finally(() => {
      void CultureService.set(getStoredLocale())
        .then(() => ApplicationConfigurationService.get())
        .then((res) => {
          storeApplicationLocalization(res);
        })
        .finally(() => setSubmitted(false));
    });
  }, []);// eslint-disable-line react-hooks/exhaustive-deps
  const getSubdomain = () => {
    const hostname = window.location.hostname;
    const regexParse = new RegExp('[a-z\-0-9]{2,63}\.[a-z\.]{2,5}$');
    const urlParts = regexParse.exec(hostname);
    return hostname.replace(urlParts[0], '').slice(0, -1);
  }
  const validateTaxCode = value => {
    let patt = /^[0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9]$/;
    if (value.length > 10) {
      patt = /^[0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][-][0-9][0-9][0-9]$/;
    }
    if (value === "") return true;
    if (patt.test(value) === false) {
      return false;
    }
    const number = Number(value.charAt(0)) * 31 + Number(value.charAt(1)) * 29 + Number(value.charAt(2)) * 23
      + Number(value.charAt(3)) * 19 + Number(value.charAt(4)) * 17 + Number(value.charAt(5)) * 13
      + Number(value.charAt(6)) * 7 + Number(value.charAt(7)) * 5 + Number(value.charAt(8)) * 3;

    return (10 - number % 11) === Number(value.charAt(9));
  }
  const handleSubmit = (event) => {
    if (!submitted) {
      setSubmitted(true);

      TenantService.getByName(tenantName).then((tenant) => {
        if (tenant.isActive && tenant.success) {
          document.cookie = `__tenant=${tenant.tenantId}`;
          AppStorage.set('tenant', tenant.tenantId);
          if (!AppStorage.get(`${tenantName}_notification`)) {
            AppStorage.set(`${tenantName}_notification`, 'true');
          }
          AppStorage.set('tenantName', tenantName);
          LoginService.login(username, password, rememberMe).then((data) => {
            if (data.result === 1) {

              AppStorage.set('UserName', username);
              AppStorage.set('Pass', password);
              setSubmitted(false);
              // getTenant();
              ApplicationConfigurationService.get().then((res) => {
                storeApplicationLocalization(res);
                window.location.hash = '/';
              });
            } else {
              let errors = 'Oops';
              if (data.description) {
                errors = data.description;
              }
              ToastService.error(translateError(errors));
              setSubmitted(false);
            }
          });
        } else {
          ToastService.error(localized('validation.taxCodeInvalid'));
          setSubmitted(false);
        }
      });
    }
    event.preventDefault();
  }

  return (
    <div className="layout-wrapper layout-overlay layout-theme-light" style={{ backgroundColor: '#fff', height: '100vh' }}>
      <LanguageSwitcher publicPage />
      <div className="layout-main p-3 h-full">
        <div className="grid h-full">
          <div className="sm:hidden md:block lg:block xl:block md:col-8 lg:col-8 xl:col-7" style={{ backgroundImage: 'url(/assets/layout/images/bg-4.png)', backgroundSize: 'contain', backgroundRepeat: 'no-repeat' }}>
            <div className="p-5">
              <div className="mt-2">
                <img src={'/assets/layout/images/logo-letter-14.png'} alt="logo" />
              </div>
            </div>
          </div>
          <div className="col-12 md:col-4 lg:col-4 xl:col-5 flex justify-content-center align-content-center flex-wrap">
            <div className="card surface-ground" style={{ width: '420px' }}>
              <div className="text-center">
                <img src={'/assets/layout/images/logo-minvoice.png'} alt="logo" />
              </div>
              <form onSubmit={handleSubmit} className="p-fluid p-2">
                <label htmlFor="tenantName" className="block text-900 font-medium mb-2">{t('common.taxCode')}</label>
                <InputText id="tenantName" type="text" className="mb-3" value={tenantName} onChange={e => setTenantName(e.target.value)} required />

                <label htmlFor="username" className="block text-900 font-medium mb-2">{t('auth.username')}</label>
                <InputText id="username" type="text" className="mb-3" value={username} onChange={e => setUsername(e.target.value)} required />

                <label htmlFor="password" className="block text-900 font-medium mb-2">{t('auth.password')}</label>
                <Password id="password" className="mb-3" value={password} onChange={e => setPassword(e.target.value)} feedback={false} toggleMask required />

                <div className="flex align-items-center justify-content-between mb-6">
                  <div className="flex align-items-center">
                    <Checkbox inputId="rememberMe" className="mr-2" onChange={e => setRememberMe(e.checked)} checked={rememberMe} />
                    <label htmlFor="rememberMe">{t('auth.rememberMe')}</label>
                  </div>
                  <Link to="/forgot-password">
                    <span className="font-medium no-underline ml-2 text-blue-500 text-right cursor-pointer">{t('auth.forgotPassword')}</span>
                  </Link>
                </div>

                <Button label={t('auth.login')} icon="pi pi-user" className="w-full" disabled={submitted} />

                <div className="text-center mt-3">
                  {t('auth.registerPrompt')} <a href="https://minvoice.vn/dang-ky-su-dung/" target="_blank" rel="noreferrer" className="font-medium no-underline ml-2 text-red-500">{t('auth.register')}</a>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
